import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, CheckCircle2, Clock, XCircle, Users, ArrowRight, ShieldAlert } from "lucide-react";
import { Layout } from "../components/Layout";
import { DonationForm } from "../components/DonationForm";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { fetchDonationStatus } from "../lib/api";

const fmtMoney = (n) =>
  n == null ? "" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 })}`;

export const SupportPage = () => {
  const location = useLocation();
  const [ret, setRet] = useState(null); // {status, data}

  useDocumentMeta({
    title: "Support This Project · Will Not Be Forgotten",
    description:
      "Help keep Will Not Be Forgotten alive. One-time or recurring donations from supporters fund this independent, one-person civic-memory archive.",
    canonical: "https://willnotbeforgotten.org/support",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sid = params.get("session_id");
    const canceled = params.get("canceled");
    if (canceled) {
      setRet({ status: "canceled" });
      return;
    }
    if (!sid) return;
    setRet({ status: "checking" });
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const data = await fetchDonationStatus(sid);
        if (data.payment_status === "paid") {
          setRet({ status: "paid", data });
          return;
        }
        if (data.payment_status === "expired" || data.status === "expired") {
          setRet({ status: "expired" });
          return;
        }
      } catch {
        /* keep trying */
      }
      attempts += 1;
      if (attempts >= 6) {
        setRet({ status: "timeout" });
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [location.search]);

  if (ret && ret.status !== "canceled") {
    return (
      <Layout headerVariant="solid">
        <ReturnPanel state={ret} />
      </Layout>
    );
  }

  return (
    <Layout headerVariant="solid">
      <section data-testid="support-hero" className="section section--no-border" style={{ paddingTop: "5rem", paddingBottom: "2rem" }}>
        <div className="shell">
          <span className="section__eyebrow">Support this project</span>
          <h1 style={{ fontSize: "clamp(2.4rem, 4.6vw, 4.25rem)", lineHeight: 1, marginBottom: "1.25rem" }}>
            Keep this <em style={{ fontStyle: "italic", color: "hsl(var(--accent))" }}>memory</em> alive.
          </h1>
          <p className="section__lede" style={{ marginTop: 0, maxWidth: 760 }}>
            Will Not Be Forgotten is built and maintained by one person — researching, writing, citing,
            and paying for the hosting that keeps it online. Your support is what turns a personal mission
            into a lasting public archive.
          </p>
          {ret?.status === "canceled" && (
            <div role="status" data-testid="support-canceled" style={{
              marginTop: "1.5rem", display: "inline-flex", alignItems: "center", gap: 8,
              padding: "0.7rem 0.95rem", fontSize: "0.9rem",
              border: "1px solid hsl(var(--line-strong))", background: "hsl(var(--bg-elev))",
              color: "hsl(var(--ink-soft))",
            }}>
              Checkout was canceled — no charge was made. You can pick up where you left off below.
            </div>
          )}
        </div>
      </section>

      <section className="section" style={{ paddingTop: "1rem" }}>
        <div className="shell">
          <div className="support-grid" style={{
            display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1.1fr)",
            gap: "3rem", alignItems: "start",
          }}>
            <div>
              <h2 style={{ marginTop: 0 }}>Where your gift goes.</h2>
              <ul style={{ listStyle: "none", padding: 0, margin: "1.5rem 0 0", display: "flex", flexDirection: "column", gap: "1.1rem" }}>
                {[
                  ["Hosting & maintenance", "Servers, the domain, and the tools that keep 130+ entries online and fast."],
                  ["Research & writing", "Time spent reading primary sources and writing the multi-paragraph histories behind each date."],
                  ["Growing the archive", "Adding new observances, anniversaries, and the people too often left out of the record."],
                ].map(([t, b]) => (
                  <li key={t} style={{ display: "flex", gap: 12 }}>
                    <Heart size={18} aria-hidden style={{ color: "hsl(var(--accent))", marginTop: 3, flexShrink: 0 }} />
                    <div>
                      <strong style={{ color: "hsl(var(--ink))", fontFamily: "var(--font-display)", fontSize: "1.05rem", fontWeight: 500 }}>{t}</strong>
                      <p style={{ margin: "2px 0 0", fontSize: "0.95rem", lineHeight: 1.55 }}>{b}</p>
                    </div>
                  </li>
                ))}
              </ul>

              <div style={{
                marginTop: "2rem", padding: "1.25rem 1.4rem",
                borderLeft: "3px solid hsl(var(--accent))", background: "hsl(var(--bg-elev))",
              }}>
                <p style={{ margin: 0, fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: "1.05rem", lineHeight: 1.5, color: "hsl(var(--ink-soft))" }}>
                  “This is a one-person project. There’s no team, no foundation, no ad money — just a belief
                  that these dates deserve to be remembered. Every gift, at any size, genuinely keeps the
                  lights on.”
                </p>
              </div>

              <p style={{ marginTop: "1.75rem", fontSize: "0.95rem" }}>
                Already given?{" "}
                <Link to="/supporters" data-testid="support-wall-link" style={{ fontWeight: 500, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  See the supporter wall <ArrowRight size={14} aria-hidden />
                </Link>
              </p>
            </div>

            <div data-testid="support-form-col">
              <DonationForm />
            </div>
          </div>
        </div>
      </section>

      <section data-testid="support-terms" className="section" style={{ background: "hsl(var(--bg-deep))" }}>
        <div className="shell" style={{ maxWidth: 860 }}>
          <span className="section__eyebrow"><ShieldAlert size={14} aria-hidden style={{ marginRight: -2 }} /> Donation terms</span>
          <h2 style={{ marginTop: 0 }}>Before you give, the fine print.</h2>
          <div style={{ marginTop: "1.5rem", display: "flex", flexDirection: "column", gap: "1rem", fontSize: "0.95rem", lineHeight: 1.65, color: "hsl(var(--ink-soft))" }}>
            <p style={{ margin: 0 }}>
              <strong style={{ color: "hsl(var(--ink))" }}>Donations are non-refundable.</strong> Gifts to Will Not Be Forgotten
              are voluntary contributions to support an independent project — not purchases of a product or service.
              Once a payment is processed it cannot be refunded.
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: "hsl(var(--ink))" }}>Recurring gifts renew automatically.</strong> If you choose monthly,
              quarterly, or yearly support, your card is charged again each period until you cancel. You can cancel a
              recurring donation at any time through the receipt emailed to you by Stripe, or by contacting the project.
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: "hsl(var(--ink))" }}>Not tax-deductible.</strong> Will Not Be Forgotten is an independent
              civic-memory project and is not a registered 501(c)(3) charity, so donations are not tax-deductible.
            </p>
            <p style={{ margin: 0 }}>
              <strong style={{ color: "hsl(var(--ink))" }}>Your privacy is yours to set.</strong> You decide what appears on the
              public supporter wall — your name, your message, and whether your amount is shown — or you can give entirely
              anonymously. Card details are handled solely by Stripe and never touch this site.
            </p>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 880px) {
          .support-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </Layout>
  );
};

const ReturnPanel = ({ state }) => {
  const data = state.data;
  const isPaid = state.status === "paid";
  const checking = state.status === "checking";
  const expired = state.status === "expired";

  const Icon = isPaid ? CheckCircle2 : checking ? Clock : XCircle;
  const tone = isPaid ? "var(--accent)" : expired ? "var(--destructive)" : "var(--muted)";

  return (
    <section data-testid="support-return" className="section" style={{ borderTop: "none", paddingTop: "5rem", minHeight: "60vh" }}>
      <div className="shell" style={{ maxWidth: 640, textAlign: "center" }}>
        <div style={{
          width: 64, height: 64, margin: "0 auto 1.5rem", borderRadius: 999,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `hsl(${tone} / 0.12)`, color: `hsl(${tone})`,
        }}>
          <Icon size={30} aria-hidden style={checking ? { animation: "wnbf-spin 1.4s linear infinite" } : undefined} />
        </div>

        {isPaid && (
          <>
            <h1 data-testid="support-thanks" style={{ fontSize: "clamp(2rem, 3.6vw, 3rem)" }}>Thank you.</h1>
            <p className="section__lede" style={{ margin: "1rem auto 0", maxWidth: 520 }}>
              Your {data?.frequency_label?.toLowerCase() === "one-time" ? "" : `${data?.frequency_label?.toLowerCase()} `}
              gift{data?.amount ? ` of ${fmtMoney(data.amount)}` : ""} helps keep this archive alive. It means more than you know.
            </p>
            <p style={{ marginTop: "1rem", fontSize: "0.9rem", color: "hsl(var(--muted))" }}>
              A receipt is on its way to your email. Your name may take a moment to appear on the supporter wall.
            </p>
          </>
        )}
        {checking && (
          <>
            <h1 style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}>Confirming your gift…</h1>
            <p className="section__lede" style={{ margin: "1rem auto 0", maxWidth: 480 }}>
              Hang tight while we confirm your payment with Stripe. This usually takes just a few seconds.
            </p>
          </>
        )}
        {(expired || state.status === "timeout") && (
          <>
            <h1 style={{ fontSize: "clamp(1.8rem, 3vw, 2.5rem)" }}>
              {expired ? "This checkout expired." : "Still processing."}
            </h1>
            <p className="section__lede" style={{ margin: "1rem auto 0", maxWidth: 500 }}>
              {expired
                ? "No charge was made. You’re welcome to try again whenever you’re ready."
                : "We couldn’t confirm the payment in time. If you completed checkout, your receipt email is the source of truth — and your gift will still appear once it settles."}
            </p>
          </>
        )}

        <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap", marginTop: "2rem" }}>
          <Link to="/supporters" className="btn btn--accent" data-testid="return-view-wall">
            <Users size={15} aria-hidden /> View the supporter wall
          </Link>
          <Link to="/support" className="btn btn--ghost" data-testid="return-give-again">
            {isPaid ? "Give again" : "Back to support"}
          </Link>
          <Link to="/" className="btn btn--ghost" data-testid="return-home">Return to the archive</Link>
        </div>
      </div>
    </section>
  );
};
